import { AnalyticsEvent, track, type AnalyticsEventName } from "@/lib/analytics"

// The Prospect Pipeline milestones the /apply client emits, in his vocabulary
// (context/cal-com.md, "BOOKING IS THE SUBMISSION"): lead_captured when valid
// contact information is saved, application_started when he proceeds beyond
// contact capture, calendar_reached when the inline booking screen renders.
// The fourth name, booking_confirmed, is the canonical conversion and is fired
// SERVER-SIDE only by the Cal webhook once the stamp has landed; nothing here
// ever emits it, and nothing here treats the form's final POST as a conversion.
//
// Why a separate module rather than three track() calls in the component: the
// ORDER of the first two is a promise the funnel depends on and the component
// cannot keep it on its own. lead_captured waits for the server to acknowledge
// the durable lead save (that is what "saved" means, and the Meta Lead pixel
// needs the server-issued dedup id that arrives with it), while the step
// advance that means "started" is a synchronous click. On a slow save the
// click lands first, and a PostHog funnel with strict ordering would then drop
// the man between its first two steps. So application_started is HELD until
// lead_captured has gone out, and released the moment it does. That sequencing
// is a pure state machine, which is why it lives here where a node eval can
// drive it without a browser.

export type MilestoneProps = Record<string, string | number | boolean | null | undefined>
export type MilestoneEmitter = (event: AnalyticsEventName, props?: MilestoneProps) => void

/** The step whose completion is the lead capture: name and email together. */
export const CONTACT_STEP_ID = "name"
/** The final step. Its "answer" is a confirmed booking, not a field. */
export const BOOKING_STEP_ID = "booking"
/** `lead_captured` has more than one door; this is the value the form sends. */
export const APPLY_LEAD_DOOR = "apply"

export type ApplyMilestones = {
  /**
   * The server acknowledged the durable lead row for this contact. Fires
   * `lead_captured` once per visit, then releases a held `application_started`.
   * Never called for a resumed application: its capture fired in the session
   * that created the row.
   */
  leadCaptured(props: MilestoneProps): void
  /**
   * He pressed Continue on the contact step with a valid name and email.
   * Fires `application_started` once per visit, immediately when the lead
   * capture has already gone out and otherwise the moment it does.
   */
  contactStepAdvanced(props: MilestoneProps): void
  /**
   * A step rendered (mount, forward or back). Fires `calendar_reached` once per
   * visit when that step is the booking step; every other step is progress the
   * existing `apply_step_viewed` already records.
   */
  stepReached(stepId: string, props: MilestoneProps): void
}

export function createApplyMilestones(emit: MilestoneEmitter = track): ApplyMilestones {
  let leadFired = false
  let startedFired = false
  let startedHeld: MilestoneProps | null = null
  let calendarFired = false

  function fireStarted(props: MilestoneProps) {
    startedFired = true
    startedHeld = null
    emit(AnalyticsEvent.ApplicationStarted, props)
  }

  return {
    leadCaptured(props) {
      if (leadFired) return
      leadFired = true
      emit(AnalyticsEvent.LeadCaptured, { ...props, door: APPLY_LEAD_DOOR })
      if (startedHeld) fireStarted(startedHeld)
    },
    contactStepAdvanced(props) {
      if (startedFired) return
      if (leadFired) fireStarted(props)
      else startedHeld = props
    },
    stepReached(stepId, props) {
      if (stepId !== BOOKING_STEP_ID || calendarFired) return
      calendarFired = true
      emit(AnalyticsEvent.CalendarReached, props)
    },
  }
}
